/**
 * Mobile drawer + accordion behavior.
 *
 * - [data-mobile-nav-trigger] toggles the drawer open/closed
 * - [data-mobile-nav-toggle] (inside an .primary-nav-mobile__item with children)
 *   toggles the submenu inside that item
 * - When open: focus is trapped within the drawer; ESC closes; resizing into
 *   desktop layout closes; the previously-set inline body overflow is preserved
 *   and restored on close.
 */

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

let priorBodyOverflow: string | null = null;

function getFocusables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
  );
}

function trapFocus(e: KeyboardEvent, drawer: HTMLElement): void {
  if (e.key !== 'Tab') return;
  const focusables = getFocusables(drawer);
  if (focusables.length === 0) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement as HTMLElement | null;

  if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

let trapHandler: ((e: KeyboardEvent) => void) | null = null;

function toggleDrawer(force?: boolean): void {
  const trigger = document.querySelector<HTMLButtonElement>('[data-mobile-nav-trigger]');
  const drawer = document.querySelector<HTMLElement>('[data-mobile-nav-drawer]');
  if (!trigger || !drawer) return;

  const isOpen = drawer.hasAttribute('data-open');
  const next = typeof force === 'boolean' ? force : !isOpen;

  if (next) {
    drawer.setAttribute('data-open', '');
    drawer.classList.remove('hidden');
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('aria-label', 'Close menu');
    priorBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus into the drawer (first focusable).
    const focusables = getFocusables(drawer);
    if (focusables.length > 0) focusables[0].focus();

    trapHandler = (e) => trapFocus(e, drawer);
    document.addEventListener('keydown', trapHandler);
  } else {
    drawer.removeAttribute('data-open');
    drawer.classList.add('hidden');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', 'Open menu');
    document.body.style.overflow = priorBodyOverflow ?? '';
    priorBodyOverflow = null;

    if (trapHandler) {
      document.removeEventListener('keydown', trapHandler);
      trapHandler = null;
    }

    // Return focus to the trigger.
    trigger.focus();
  }
}

function toggleItem(button: HTMLButtonElement): void {
  const li = button.closest<HTMLElement>('.primary-nav-mobile__item');
  if (!li) return;
  const submenu = li.querySelector<HTMLElement>('[data-mobile-nav-submenu]');
  if (!submenu) return;

  // [data-open] drives everything visual (height animation, chevron rotation,
  // mustard row) via the mobile-drawer rules in tailwind.css.
  const next = button.getAttribute('aria-expanded') !== 'true';
  button.setAttribute('aria-expanded', next ? 'true' : 'false');
  li.toggleAttribute('data-open', next);
}

function closeDrawerOnViewportChange(): void {
  // Close when crossing into desktop layout (xl = 1280px).
  const mq = window.matchMedia('(min-width: 1280px)');
  const onChange = () => {
    if (mq.matches) toggleDrawer(false);
  };
  mq.addEventListener('change', onChange);
  onChange();
}

export function initMobileNav(): void {
  const trigger = document.querySelector<HTMLButtonElement>('[data-mobile-nav-trigger]');
  if (!trigger) return;

  trigger.addEventListener('click', () => toggleDrawer());

  document.querySelectorAll<HTMLButtonElement>('[data-mobile-nav-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => toggleItem(btn));
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') toggleDrawer(false);
  });

  closeDrawerOnViewportChange();
}
